import type { Document as DocumentT } from "@prismicio/types-internal/lib/content"
import type { CustomType } from "@prismicio/types-internal/lib/customtypes"
import { createContext, type MutableRefObject } from "react"

import type { DocumentMeta } from "./service"

/**
 * Allows the UID Field to directly communicate its important events
 * with an optional, external party without polluting all the parents in between.
 */

interface UIDContextValue {
  editedUID: MutableRefObject<boolean> // Uses Ref instead of state to enable synchronous state access after an update. Beware components accessing Refs do not subscribe to value updates automatically.
  onEditUID: () => void
}

export const UIDContext = createContext<UIDContextValue>({
  editedUID: { current: false },
  onEditUID: () => {},
})

export function hasUIDField(customType: CustomType) {
  return Object.values(customType.json).some(
    (section) => section["uid"]?.type === "UID",
  )
}

interface isMissingUIDArgs {
  content: DocumentT
  customType: CustomType
}

export function isMissingUID(args: isMissingUIDArgs) {
  const { content, customType } = args

  return hasUIDField(customType) && !content["uid"]
}

export interface GetConflictingUidDocumentsArgs {
  customTypeId: string
  languageId: string
  documentId: string | undefined
  /** The list of documents to check for UID conflicts */
  documentsWithUid: DocumentMeta[]
  uid: string
}

/**
 * Create a list of documents with the same UID as the given document.
 */
export function getConflictingUidDocuments(
  args: GetConflictingUidDocumentsArgs,
): DocumentMeta[] {
  const { documentId, documentsWithUid, uid } = args

  return documentsWithUid.filter(
    (document) =>
      documentId !== document.id &&
      hasNonArchivedVersionsMatchingUID({ document, uid }) &&
      hasSameAttributes({ ...args, document }),
  )
}

interface HasSameAttributesArgs {
  customTypeId: string | undefined
  languageId: string
  document: DocumentMeta
}

function hasSameAttributes(attrs: HasSameAttributesArgs) {
  const { customTypeId, languageId, document } = attrs

  const hasSameCustomTypeId = customTypeId === document.custom_type_id
  const hasSameLocale = languageId === document.language.id

  return hasSameCustomTypeId && hasSameLocale
}

interface HasNonArchivedVersionsMatchingUIDArgs {
  document: DocumentMeta
  uid: string
}

function hasNonArchivedVersionsMatchingUID(
  args: HasNonArchivedVersionsMatchingUIDArgs,
) {
  const { document, uid } = args

  const matchingVersions = document.versions.filter(
    (version) => version.uid === uid,
  )

  // No matching version means the uid must have been in non-interesting versions, thus they are not archived and not allowed
  if (matchingVersions.length === 0) {
    return true
  }

  return matchingVersions.some((version) => version.status !== "archived")
}
